import * as React from "react";
import SideNavigation from "@amzn/awsui-components-react/polaris/side-navigation";
import Header from "@amzn/awsui-components-react/polaris/header";
import Box from "@amzn/awsui-components-react/polaris/box";
import Container from "@amzn/awsui-components-react/polaris/container";
import Input from "@amzn/awsui-components-react/polaris/input";
import Button from "@amzn/awsui-components-react/polaris/button";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Spinner from "@amzn/awsui-components-react/polaris/spinner";
import Alert from "@amzn/awsui-components-react/polaris/alert";
import PageShell from "../../../../global/components/PageShell";
import CustomerInfoHeader from "./CustomerInfoHeader";
import DrugJourneyCard from "./DrugJourneyCard";
import { CustomerJourneyPageState, CustomerJourneyResponse } from "./journey-types";
import { API } from "aws-amplify";

class CustomerJourneyPage extends React.Component<any, CustomerJourneyPageState> {
  constructor(props: any) {
    super(props);
    this.state = {
      customerId: "",
      isLoading: false,
      errorMessage: "",
      journeyData: null,
      navOpen: true,
    };
  }

  private fetchCustomerJourney = async (): Promise<void> => {
    const trimmedId = this.state.customerId.trim();
    if (!trimmedId) return;

    this.setState({ isLoading: true, errorMessage: "", journeyData: null });

    try {
      const data: CustomerJourneyResponse = await API.get(
        "ApaqPortalAPI",
        `/customer-journey/${trimmedId}`,
        {}
      );

      if (data.errors && data.errors.length > 0 && (!data.drugs || data.drugs.length === 0)) {
        const errorMsg = data.errors
          .map((e) => `[${e.source || "Unknown"}] ${e.message || "Unknown error"}`)
          .join("; ");
        this.setState({ errorMessage: errorMsg, isLoading: false });
      } else {
        this.setState({ journeyData: data, isLoading: false });
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : "An error occurred while fetching customer journey";
      this.setState({
        errorMessage: msg,
        isLoading: false,
      });
    }
  };

  private isSubmitDisabled(): boolean {
    return this.state.customerId.trim() === "" || this.state.isLoading;
  }

  private renderMedications(medicationsStr: string) {
    // Parse the medications string — it's in a Java toString() format like "[Medication(name=..., status=...)]"
    // Extract medication entries
    const medRegex = /Medication\(([^)]+)\)/g;
    const meds: Array<{ [key: string]: string }> = [];
    let match;
    while ((match = medRegex.exec(medicationsStr)) !== null) {
      const fields: { [key: string]: string } = {};
      match[1].split(", ").forEach(pair => {
        const eqIdx = pair.indexOf("=");
        if (eqIdx > 0) {
          fields[pair.substring(0, eqIdx).trim()] = pair.substring(eqIdx + 1).trim();
        }
      });
      meds.push(fields);
    }

    if (meds.length === 0) return null;

    return (
      <Container header={<Header variant="h2"><span style={{ display: "flex", alignItems: "center", gap: "8px" }}><span style={{ fontSize: "20px" }}>📋</span>Health Profile Medications</span></Header>}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "12px" }}>
          {meds.map((med, idx) => (
            <div key={idx} style={{
              padding: "14px 18px",
              backgroundColor: "#f8f9fa",
              borderRadius: "10px",
              border: "1px solid #e9ebed",
              borderLeft: `4px solid ${med.status === "ACTIVE" ? "#388e3c" : "#9e9e9e"}`,
            }}>
              <div style={{ fontWeight: 600, fontSize: "13px", color: "#16191f", marginBottom: "6px" }}>
                {med.name || "Unknown Medication"}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", fontSize: "11px", color: "#5f6b7a" }}>
                {med.status && <span style={{ padding: "2px 8px", borderRadius: "4px", backgroundColor: med.status === "ACTIVE" ? "#e8f5e9" : "#f5f5f5", color: med.status === "ACTIVE" ? "#388e3c" : "#757575" }}>{med.status}</span>}
                {med.source && <span>Source: {med.source}</span>}
                {med.rxcui && med.rxcui !== "null" && <span>RxCUI: {med.rxcui}</span>}
                {med.daysSupply && med.daysSupply !== "0" && <span>{med.daysSupply} day supply</span>}
              </div>
            </div>
          ))}
        </div>
      </Container>
    );
  }

  private renderSummaryBar(data: CustomerJourneyResponse) {
    const totalDrugs = data.drugs.length;
    const activePrescriptions = data.drugs.reduce((sum, d) =>
      sum + d.prescriptions.filter(p => p.prescriptionStatus === "ACTIVE").length, 0);
    const totalFills = data.drugs.reduce((sum, d) =>
      sum + d.prescriptions.reduce((s, p) => s + p.fills.length, 0), 0);
    const purchasedFills = data.drugs.reduce((sum, d) =>
      sum + d.prescriptions.reduce((s, p) => s + p.fills.filter(f => f.fillStatus === "PURCHASED").length, 0), 0);
    const shippedOrders = data.drugs.reduce((sum, d) =>
      sum + d.prescriptions.reduce((s, p) =>
        s + p.fills.filter(f => f.order?.shipment?.shipmentCondition?.startsWith("6:")).length, 0), 0);

    const cards = [
      { label: "Total Drugs", value: totalDrugs, color: "#1976d2", bg: "linear-gradient(135deg, #1976d2, #42a5f5)" },
      { label: "Active Rx", value: activePrescriptions, color: "#388e3c", bg: "linear-gradient(135deg, #388e3c, #66bb6a)" },
      { label: "Total Fills", value: totalFills, color: "#7b1fa2", bg: "linear-gradient(135deg, #7b1fa2, #ab47bc)" },
      { label: "Purchased", value: purchasedFills, color: "#f57c00", bg: "linear-gradient(135deg, #f57c00, #ffb74d)" },
      { label: "Shipped", value: shippedOrders, color: "#00838f", bg: "linear-gradient(135deg, #00838f, #4dd0e1)" },
    ];

    return (
      <div style={{ display: "flex", gap: "12px" }}>
        {cards.map((card) => (
          <div
            key={card.label}
            style={{
              flex: 1,
              background: card.bg,
              borderRadius: "12px",
              padding: "16px 20px",
              color: "#ffffff",
              textAlign: "center",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            }}
          >
            <div style={{ fontSize: "28px", fontWeight: 700 }}>{card.value}</div>
            <div style={{ fontSize: "12px", fontWeight: 500, opacity: 0.9, marginTop: "4px" }}>{card.label}</div>
          </div>
        ))}
      </div>
    );
  }

  render() {
    const { customerId, isLoading, errorMessage, journeyData, navOpen } = this.state;

    return (
      <PageShell
        navigationOpen={navOpen}
        onNavigationChange={(open) => this.setState({ navOpen: open })}
        navigation={
          <SideNavigation
            header={{ text: "Customer Journey", href: "#" }}
            activeHref="#customer-journey"
            items={[
              { type: "link", text: "Journey Dashboard", href: "#customer-journey" },
            ]}
          />
        }
        content={
          <div>
            <Box padding={{ top: "l", bottom: "l" }}>
              <Header
                variant="h1"
                description="Visualize a customer's prescription-to-shipment journey"
              >
                Customer Journey Dashboard
              </Header>
            </Box>

            <SpaceBetween size="l">
              <Container>
                <SpaceBetween size="s" direction="horizontal">
                  <div style={{ width: "600px" }}>
                    <Input
                      value={customerId}
                      onChange={({ detail }) => this.setState({ customerId: detail.value })}
                      placeholder="Enter Customer ID, Person ID, or Profile ID"
                    />
                  </div>
                  <Button
                    variant="primary"
                    onClick={this.fetchCustomerJourney}
                    disabled={this.isSubmitDisabled()}
                    loading={isLoading}
                  >
                    View Journey
                  </Button>
                </SpaceBetween>
              </Container>

              {isLoading && (
                <Box textAlign="center" padding="xxl">
                  <Spinner size="large" />
                </Box>
              )}

              {errorMessage && (
                <Alert
                  type="error"
                  dismissible
                  onDismiss={() => this.setState({ errorMessage: "" })}
                >
                  {errorMessage}
                </Alert>
              )}

              {journeyData && (
                <SpaceBetween size="l">
                  {journeyData.errors && journeyData.errors.length > 0 && (
                    <Alert type="warning">
                      Some data may be incomplete: {journeyData.errors.map((e) => `[${e.source}] ${e.message}`).join("; ")}
                    </Alert>
                  )}
                  {this.renderSummaryBar(journeyData)}
                  <CustomerInfoHeader data={journeyData} />
                  {journeyData.medications && this.renderMedications(journeyData.medications)}
                  {journeyData.drugs.length === 0 ? (
                    <Alert type="info">No drugs found for this customer</Alert>
                  ) : (
                    journeyData.drugs.map((drug) => (
                      <DrugJourneyCard key={drug.drugId} drug={drug} />
                    ))
                  )}
                </SpaceBetween>
              )}
            </SpaceBetween>
          </div>
        }
      />
    );
  }
}

export default CustomerJourneyPage;
